import { useEffect, useState } from "react"
import { AppBar, Box, Button, Grid, Tab, Tabs } from "@mui/material"
import { doc, getDoc, setDoc } from "firebase/firestore"
import { useNavigate, useParams } from "react-router-dom"

import { db } from "../../firebase"
import { handleUpload } from "../../utils"
import { LoadingService } from "react-ui-components/components/LoadingService"
import { PopupService } from "react-ui-components/components/PopupService"

import { WheelTabPanel, wheelTabA11yProps } from "./WheelTabPanel"
import { WheelSetupPanel } from "./WheelSetupPanel"
import { WheelPrizeManager } from "./WheelPrizeManager"
import { WheelPresentationConfig } from "./WheelPresentationConfig"
import { WheelWinnerNotificationConfig } from "./WheelWinnerNotificationConfig"
import { WheelAnalytics } from "./WheelAnalytics"
import { WheelLivePreview } from "./WheelLivePreview"
import { WheelResetPanel } from "./WheelResetPanel"
import {
  DEFAULT_CONFIG,
  DEFAULT_PRESENTATION,
  DEFAULT_WINNER_NOTIFICATIONS,
  WheelConfigFormData,
  WheelPrizeFormData,
} from "./wheelPrizeTypes"
import { cleanPrizeForSave, normalizeLoadedPrizes, validateConfig } from "./wheelPrizeValidation"

const WHEEL_CONFIGS_COLLECTION = "wheelConfigs"
const WHEEL_IMAGES_BUCKET = "wheel"

const TABS = ["Setup", "Prizes", "Presentation", "Winner notifications", "Analytics", "Resets"] as const

/**
 * Admin workspace for a single `wheelConfigs/{id}` doc. An admin can create several of these
 * (see WheelList), each scoped by `location`/`language`, and the `spinWheel` Cloud Function
 * resolves the best match for a given user (falling back to the wheel marked `isDefault`).
 * Disabled by default: a doc with `isEnabled` unset means that wheel stays hidden from users
 * (see wheelSpin.ts).
 */
export const WheelConfig = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [config, setConfig] = useState<WheelConfigFormData>(DEFAULT_CONFIG)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState(0)
  const [validationErrors, setValidationErrors] = useState<string[]>([])

  useEffect(() => {
    if (!id) {
      navigate("/wheel-config")
      return
    }

    const load = async () => {
      try {
        const snapshot = await getDoc(doc(db, WHEEL_CONFIGS_COLLECTION, id))
        if (snapshot.exists()) {
          const data = snapshot.data() as any
          setConfig({
            name: typeof data.name === "string" && data.name ? data.name : "Untitled wheel",
            location: data.location && typeof data.location === "object" ? data.location : {},
            language: typeof data.language === "string" ? data.language : "",
            isDefault: data.isDefault === true,
            isEnabled: data.isEnabled === true,
            dailySpinLimit: typeof data.dailySpinLimit === "number" ? data.dailySpinLimit : 1,
            cooldownMinutes: typeof data.cooldownMinutes === "number" ? data.cooldownMinutes : 0,
            campaignStartAtMillis: typeof data.campaignStartAtMillis === "number" ? data.campaignStartAtMillis : null,
            campaignEndAtMillis: typeof data.campaignEndAtMillis === "number" ? data.campaignEndAtMillis : null,
            dailyGrandPrizeLimit: typeof data.dailyGrandPrizeLimit === "number" ? data.dailyGrandPrizeLimit : null,
            prizes: normalizeLoadedPrizes(data.prizes),
            presentation: { ...DEFAULT_PRESENTATION, ...(data.presentation ?? {}) },
            winnerNotifications: { ...DEFAULT_WINNER_NOTIFICATIONS, ...(data.winnerNotifications ?? {}) },
          })
        }
      } catch (error: any) {
        console.error("Error loading wheel config:", error)
        PopupService.pushPopup(error.message)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [id, navigate])

  const patchConfig = (patch: Partial<WheelConfigFormData>) => setConfig((prev) => ({ ...prev, ...patch }))
  const patchPrizes = (prizes: WheelPrizeFormData[]) => setConfig((prev) => ({ ...prev, prizes }))
  const patchPresentation = (patch: Partial<WheelConfigFormData["presentation"]>) =>
    setConfig((prev) => ({ ...prev, presentation: { ...prev.presentation, ...patch } }))
  const patchWinnerNotifications = (patch: Partial<WheelConfigFormData["winnerNotifications"]>) =>
    setConfig((prev) => ({ ...prev, winnerNotifications: { ...prev.winnerNotifications, ...patch } }))

  const handleSave = async () => {
    if (!id) {
      return
    }
    const errors = validateConfig(config)
    if (errors.length > 0) {
      setValidationErrors(errors)
      setTab(0)
      return
    }
    setValidationErrors([])

    setSaving(true)
    LoadingService.setLoading(true)
    try {
      // Uploads pending (base64) prize/banner images; already-hosted URLs pass through unchanged.
      const prizesWithUploadedImages = await Promise.all(
        config.prizes.map(async (prize) => {
          if (!prize.imageUrl) {
            return prize
          }
          const uploadedUrl = (await handleUpload({
            image: prize.imageUrl,
            id: prize.id,
            fieldName: "image",
            imagesBucket: WHEEL_IMAGES_BUCKET,
          })) as string
          return { ...prize, imageUrl: uploadedUrl }
        }),
      )

      const bannerImageUrl = config.presentation.bannerImageUrl
        ? ((await handleUpload({
            image: config.presentation.bannerImageUrl,
            id: "campaign-banner",
            fieldName: "image",
            imagesBucket: WHEEL_IMAGES_BUCKET,
          })) as string)
        : null

      const fabIconUrl = config.presentation.fabIconUrl
        ? ((await handleUpload({
            image: config.presentation.fabIconUrl,
            id: "spin-fab-icon",
            fieldName: "image",
            imagesBucket: WHEEL_IMAGES_BUCKET,
          })) as string)
        : null

      await setDoc(
        doc(db, WHEEL_CONFIGS_COLLECTION, id),
        {
          name: config.name,
          location: config.location,
          language: config.language,
          isDefault: config.isDefault,
          isEnabled: config.isEnabled,
          dailySpinLimit: config.dailySpinLimit,
          cooldownMinutes: config.cooldownMinutes,
          campaignStartAtMillis: config.campaignStartAtMillis,
          campaignEndAtMillis: config.campaignEndAtMillis,
          dailyGrandPrizeLimit: config.dailyGrandPrizeLimit,
          prizes: prizesWithUploadedImages.map(cleanPrizeForSave),
          presentation: { ...config.presentation, bannerImageUrl, fabIconUrl },
          winnerNotifications: config.winnerNotifications,
        },
        { merge: true },
      )

      setConfig((prev) => ({
        ...prev,
        prizes: prizesWithUploadedImages,
        presentation: { ...prev.presentation, bannerImageUrl, fabIconUrl },
      }))
      PopupService.pushPopup("Wheel configuration saved.")
    } catch (error: any) {
      console.error("Error saving wheel config:", error)
      PopupService.pushPopup(error.message)
    } finally {
      setSaving(false)
      LoadingService.setLoading(false)
    }
  }

  if (loading) {
    return null
  }

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Button size="small" onClick={() => navigate("/wheel-config")} sx={{ mb: 1 }}>
          ← All wheels
        </Button>
        <Box component="h1" sx={{ fontSize: 24, fontWeight: 600, m: 0 }}>
          {config.name || "Daily Spin Experience"}
        </Box>
        <Box component="p" sx={{ color: "text.secondary", mt: 0.5 }}>
          Configure cadence, prize inventory, linked entities, presentation, odds, and winner broadcasts.
        </Box>
      </Box>

      {validationErrors.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {validationErrors.map((message) => (
            <Box key={message} sx={{ color: "error.main", fontSize: 14 }}>
              • {message}
            </Box>
          ))}
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <AppBar position="static" color="default">
            <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto">
              {TABS.map((label, index) => (
                <Tab key={label} label={label} {...wheelTabA11yProps(index)} />
              ))}
            </Tabs>
          </AppBar>

          <WheelTabPanel value={tab} index={0}>
            <WheelSetupPanel config={config} onChange={patchConfig} />
          </WheelTabPanel>
          <WheelTabPanel value={tab} index={1}>
            <WheelPrizeManager prizes={config.prizes} onChange={patchPrizes} />
          </WheelTabPanel>
          <WheelTabPanel value={tab} index={2}>
            <WheelPresentationConfig presentation={config.presentation} onChange={patchPresentation} />
          </WheelTabPanel>
          <WheelTabPanel value={tab} index={3}>
            <WheelWinnerNotificationConfig config={config.winnerNotifications} onChange={patchWinnerNotifications} />
          </WheelTabPanel>
          <WheelTabPanel value={tab} index={4}>
            <WheelAnalytics prizes={config.prizes} />
          </WheelTabPanel>
          <WheelTabPanel value={tab} index={5}>
            <WheelResetPanel />
          </WheelTabPanel>

          <Box sx={{ mt: 3 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              Save
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: "sticky" }, top: { md: 16 } }}>
            <WheelLivePreview
              presentation={config.presentation}
              prizes={config.prizes}
              winnerNotifications={config.winnerNotifications}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default WheelConfig
